import { useEffect } from "react";
import "./GlobalTooltip.css";

/**
 * Application-wide styled tooltip engine.
 *
 * Mounted once at the app root. Uses event delegation on <document>, so it
 * covers every element in the app with zero per-screen changes:
 *   - Any element with a native `title` is upgraded on hover — the title is
 *     moved to `data-tip` (which suppresses the browser's default tooltip) and
 *     mirrored to `aria-label` for screen readers, then `title` is removed.
 *   - Any element with `data-tip="..."` shows the styled tooltip directly.
 *   - Opt out on a subtree with `data-tip=""` (empty) or `data-no-tip`.
 *
 * A single floating element is positioned with `position: fixed`, so it is
 * never clipped by `overflow` containers and is flipped / clamped to stay in
 * the viewport. Shows instantly (no hover delay).
 */
const GlobalTooltip = () => {
  useEffect(() => {
    const tip = document.createElement("div");
    tip.className = "app-tip";
    tip.setAttribute("role", "tooltip");
    document.body.appendChild(tip);

    let current: HTMLElement | null = null;

    const hide = () => {
      current = null;
      tip.classList.remove("app-tip--show");
    };

    const place = (el: HTMLElement) => {
      const r = el.getBoundingClientRect();
      const tr = tip.getBoundingClientRect();
      const gap = 8;
      const margin = 6;
      let placement: "top" | "bottom" = "top";
      let top = r.top - tr.height - gap;
      if (top < margin) {
        top = r.bottom + gap; // not enough room above → flip below
        placement = "bottom";
      }
      let left = r.left + r.width / 2 - tr.width / 2;
      left = Math.max(margin, Math.min(left, window.innerWidth - tr.width - margin));
      tip.style.top = Math.round(top) + "px";
      tip.style.left = Math.round(left) + "px";
      tip.setAttribute("data-placement", placement);
    };

    const show = (el: HTMLElement, text: string) => {
      current = el;
      tip.textContent = text;
      // ensure it's laid out (dimensions) before measuring for placement
      place(el);
      requestAnimationFrame(() => {
        if (current === el) {
          place(el);
          tip.classList.add("app-tip--show");
        }
      });
    };

    // Walk up from the hovered node to find the nearest element that should
    // carry a tooltip, upgrading native `title` → `data-tip` along the way.
    const resolve = (start: EventTarget | null): { el: HTMLElement; text: string } | null => {
      let el = start as HTMLElement | null;
      while (el && el.nodeType === 1 && el !== document.body) {
        if (el.hasAttribute("data-no-tip")) return null;

        const title = el.getAttribute("title");
        if (title !== null && title.trim() !== "") {
          el.setAttribute("data-tip", title);
          if (!el.getAttribute("aria-label") && !el.getAttribute("aria-labelledby")) {
            el.setAttribute("aria-label", title);
          }
          el.removeAttribute("title");
        } else if (title !== null) {
          // empty title — just drop it so it can't render a native tooltip
          el.removeAttribute("title");
        }

        if (el.hasAttribute("data-tip")) {
          const text = (el.getAttribute("data-tip") || "").trim();
          if (!text) return null; // data-tip="" = explicit opt-out
          // Suppress a redundant tooltip that just repeats the element's own
          // visible label (e.g. a "Cancel" button titled "Cancel"). Keep it when
          // the label is truncated (overflowing), since then the tip shows more.
          const own = (el.textContent || "").replace(/\s+/g, " ").trim();
          const truncated = el.scrollWidth > el.clientWidth + 1;
          if (own && !truncated && own.toLowerCase() === text.toLowerCase()) return null;
          return { el, text };
        }
        el = el.parentElement;
      }
      return null;
    };

    const onOver = (e: MouseEvent) => {
      const found = resolve(e.target);
      if (!found) {
        if (current) hide();
        return;
      }
      if (found.el !== current) show(found.el, found.text);
    };

    const onOut = (e: MouseEvent) => {
      if (!current) return;
      const to = e.relatedTarget as Node | null;
      if (to && current.contains(to)) return;
      hide();
    };

    const onFocusIn = (e: FocusEvent) => {
      const found = resolve(e.target);
      if (found) show(found.el, found.text);
    };

    document.addEventListener("mouseover", onOver, true);
    document.addEventListener("mouseout", onOut, true);
    document.addEventListener("focusin", onFocusIn, true);
    document.addEventListener("focusout", hide, true);
    document.addEventListener("click", hide, true);
    window.addEventListener("scroll", hide, true);
    window.addEventListener("resize", hide);

    return () => {
      document.removeEventListener("mouseover", onOver, true);
      document.removeEventListener("mouseout", onOut, true);
      document.removeEventListener("focusin", onFocusIn, true);
      document.removeEventListener("focusout", hide, true);
      document.removeEventListener("click", hide, true);
      window.removeEventListener("scroll", hide, true);
      window.removeEventListener("resize", hide);
      tip.remove();
    };
  }, []);

  return null;
};

export default GlobalTooltip;
